/**
 * The activity feed: one `Feed` per turn, upserted from `activity` wire
 * frames plus a client-side lifecycle App.tsx drives alongside `chatStore`'s
 * own `beginTurn()`/`endTurn()`. Design doc §5.2.
 *
 * Three rules make this store, and each is pinned by a fixture in
 * `activity.test.ts`:
 *
 * **(a) upsert by `id`, phase is monotonic.** `running(0) < done(1) =
 * failed(1)` (§3.4) — a frame that would lower a step's phase is dropped.
 * `seq` plays no part here; idempotency comes from the id, exactly like the
 * board's `pending_id` (§2.5).
 *
 * **(b) a group opens when a `running` step arrives and none is already
 * running** (§2.4). There is no `group` field on the wire — `ToolNode` runs a
 * batch with `asyncio.gather`, so every `running` of one round lands before
 * the first `done`, and the client reconstructs the boundary from that.
 *
 * **(c) a repeated `started` is a reconnect, never a wipe** (§3.4). `begin()`
 * and the wire's own `started` frame both go through the same
 * "create if missing" path, so neither one ever clears a feed that already
 * has steps in it.
 *
 * **Anchoring a finished feed.** `chatStore.restore()` stamps `turn: 0` on
 * every bubble it rebuilds after an F5 (`chatStore.ts:194`), so a feed may
 * not be found again by its turn NUMBER once the page has reloaded. Instead a
 * finished feed remembers the `id` of the last chat bubble that stood above
 * it (`anchorId`) — message ids survive `restore()` — and `Thread.tsx` draws
 * it right after that bubble. A *live* feed has no anchor: it always renders
 * at the bottom, the slot `ThinkingLine` used to own.
 *
 * **Reasoning rides on the SAME `Feed`, not a second store**
 * (reasoning-summary design doc §10, R6). The turn lifecycle, the `anchorId`
 * dance and the restored-feed turn-collision fix above are already debugged
 * here; a second store would have to earn all three again. `apply`/`begin`/
 * `finish` stay about the activity feed exactly as before — `applyReasoning`
 * and `toggleReasoning` are the reasoning block's own actions, upserting into
 * the same `feeds` array by the same `turn` key.
 */

import { create } from 'zustand';
import type { ActivityFrame, ActivitySummary } from './types';
import type { ReasoningFrame } from './reasoningTypes';

export type Phase = 'running' | 'done' | 'failed';

export type Step = {
  id: string;
  /** Kept as the raw wire string — see `types.ts` — but `activityStore` and
   *  the rows only ever branch on the three known values. */
  phase: string;
  text: string;
  icon: string;
  ms: number | null;
  group: number;
};

export type Feed = {
  turn: number;
  steps: Step[];
  openGroup: number;
  startedAt: number;
  finished: boolean;
  summary: ActivitySummary | null;
  collapsed: boolean;
  /** Set once, by `finish()`, from the last chat bubble on screen at that
   *  moment. `null` while the feed is live or was never anchored (e.g. it
   *  arrived from `restore()` with no matching bubble any more). */
  anchorId: string | null;
  /** `true` once at least one real wire frame has been applied — `begin()`
   *  alone (called synchronously at turn start, before any frame can have
   *  arrived) does not set this. `Thread.tsx`'s fallback to `ThinkingLine`
   *  reads this, not mere presence: a legacy graph's feed exists (because
   *  `begin()` runs unconditionally) but never receives a frame (§5.4). */
  hasFrames: boolean;
  /** Per-round summaries of the model's own thinking (design doc §10.3),
   *  upserted by `n` and kept SORTED by `n` — the summariser calls race each
   *  other, so a frame can arrive out of order, and the block must still read
   *  top to bottom in round order. */
  reasoning: ReasoningRound[];
  /** `true` once a `state: "done"` reasoning frame arrived, or `finish()`
   *  stamped one itself because the wire's own never came (killed container —
   *  the same fallback `summary` gets from `summaryFromSteps`, §2.11). */
  reasoningDone: boolean;
  /** Wall-clock ms of the turn, from the `done` frame's own `ms` (§10.3) —
   *  never a sum of anything. `null` until `reasoningDone`. */
  reasoningMs: number | null;
  /** Independent of `collapsed`: the reasoning block and the activity feed
   *  auto-collapse on the same 600 ms timer (§10.4) but a manual tap on one
   *  must not touch the other's pending timer or its open/closed state. */
  reasoningCollapsed: boolean;
  /** Same signal as `hasFrames`, scoped to reasoning: `true` only once a real
   *  `reasoning` wire frame has landed. `ReasoningBlock` reads this to decide
   *  whether it exists AT ALL — a legacy graph, or the flag turned off,
   *  ships no frame and the block is simply absent (§10.7). */
  hasReasoningFrames: boolean;
};

export type ReasoningRound = { n: number; text: string };

export type ActivityState = {
  feeds: Feed[];
  begin: (turn: number) => void;
  apply: (frame: ActivityFrame, turn: number) => void;
  /** The reasoning block's own upsert — same feed, same `turn` key, never a
   *  second store (§10, R6). */
  applyReasoning: (frame: ReasoningFrame, turn: number) => void;
  /** Called once, from the same place `endTurn()` is — the spinner gates on
   *  this, not on the wire's own `finished` frame (§2.11): a killed container
   *  or a dropped stream must still close the feed. `anchorId` and
   *  `threadId` are optional so the wire-triggered path (inside `apply`,
   *  which cannot know either) can share the same action. Also stamps
   *  `reasoningDone`/`reasoningMs` with the same fallback, and schedules the
   *  reasoning block's own (independent) auto-collapse timer. */
  finish: (turn: number, opts?: { anchorId?: string | null; threadId?: string | null }) => void;
  toggle: (turn: number) => void;
  toggleReasoning: (turn: number) => void;
  restore: (threadId: string) => void;
  reset: () => void;
};

const AUTO_COLLAPSE_MS = 600;
const PERSIST_PREFIX = 'ph.activity.';
// Recalculated for reasoning-summary (design doc §10.9, R8): adding up to
// three ~160-char round summaries per turn is what pushed this DOWN from 12.
// 8 × (steps ~250 B + summaries ~480 B) ≈ 5.8 KB, comfortably under
// `MAX_PERSISTED_BYTES` — and the shrink-until-it-fits loop below stays the
// backstop for a turn richer than the estimate.
const MAX_PERSISTED_TURNS = 8;
const MAX_PERSISTED_BYTES = 8 * 1024;
/** §10.9/R8: at most this many of a turn's most recent reasoning rounds
 *  persist, each capped at this many characters — a long trip-planning turn
 *  otherwise blows the byte budget on prose alone. */
const MAX_PERSISTED_REASONING_ROUNDS = 3;
const MAX_PERSISTED_REASONING_CHARS = 160;

/** `running(0) < done(1) = failed(1)`; an unrecognised phase reads as 0 so it
 *  can never downgrade a step that has already settled (§3.4). */
function phaseRank(phase: string): number {
  return phase === 'done' || phase === 'failed' ? 1 : 0;
}

function makeFeed(turn: number): Feed {
  return {
    turn,
    steps: [],
    openGroup: 0,
    startedAt: Date.now(),
    finished: false,
    summary: null,
    collapsed: false,
    anchorId: null,
    hasFrames: false,
    reasoning: [],
    reasoningDone: false,
    reasoningMs: null,
    reasoningCollapsed: false,
    hasReasoningFrames: false,
  };
}

/** Only TERMINAL steps count, matching the backend's own `emit_step` (it
 *  increments `acc["steps"]` on `done`/`failed`, never on `running`). This
 *  fallback runs precisely when the wire's own `finished` frame never
 *  arrived — the killed-container case (§2.11) — which is also when a step is
 *  most likely to still be `running`, so counting it would over-report. */
function summaryFromSteps(feed: Feed): ActivitySummary {
  const terminal = feed.steps.filter((s) => s.phase !== 'running');
  return {
    steps: terminal.length,
    ms: Math.max(0, Date.now() - feed.startedAt),
    failed: feed.steps.filter((s) => s.phase === 'failed').length,
  };
}

/** Upserts one step into `feed.steps`/`feed.openGroup`, mutating both — the
 *  caller owns a fresh clone of each (the same pattern `surfaceStore.upsert`
 *  uses), so mutating in place here costs nothing extra. */
function applyStep(feed: Feed, incoming: { id: string; phase: string; text: string; icon: string; ms: number | null }): void {
  const index = feed.steps.findIndex((s) => s.id === incoming.id);
  if (index === -1) {
    // A brand-new step decides the grouping: it joins the open group unless
    // no step is currently running, in which case it opens the next one.
    const anyRunning = feed.steps.some((s) => s.phase === 'running');
    if (incoming.phase === 'running' ? !anyRunning : feed.openGroup === 0) {
      feed.openGroup += 1;
    }
    feed.steps.push({ ...incoming, group: feed.openGroup });
    return;
  }
  const existing = feed.steps[index]!;
  if (phaseRank(incoming.phase) < phaseRank(existing.phase)) return; // downgrade ignored
  feed.steps[index] = {
    ...existing,
    phase: incoming.phase,
    text: incoming.text,
    icon: incoming.icon,
    ms: incoming.ms ?? existing.ms,
  };
}

const collapseTimers = new Map<number, ReturnType<typeof setTimeout>>();
/** The reasoning block's own timers — kept SEPARATE from `collapseTimers` so
 *  a manual tap on one block (`toggle`/`toggleReasoning`) never cancels the
 *  other's still-pending auto-collapse, even though both are scheduled from
 *  the same `finish()` call at the same delay (§10.4). */
const reasoningCollapseTimers = new Map<number, ReturnType<typeof setTimeout>>();

function clearCollapseTimer(turn: number): void {
  const timer = collapseTimers.get(turn);
  if (timer) clearTimeout(timer);
  collapseTimers.delete(turn);
}

function clearReasoningCollapseTimer(turn: number): void {
  const timer = reasoningCollapseTimers.get(turn);
  if (timer) clearTimeout(timer);
  reasoningCollapseTimers.delete(turn);
}

function clearAllCollapseTimers(): void {
  for (const timer of collapseTimers.values()) clearTimeout(timer);
  collapseTimers.clear();
  for (const timer of reasoningCollapseTimers.values()) clearTimeout(timer);
  reasoningCollapseTimers.clear();
}

/** One persisted step: icon for the collapsed chip, text for an expanded
 *  restored feed — dropping the text left a reopened summary showing blank
 *  rows (check marks with nothing beside them). */
type PersistedStep = { icon: string; text: string };

/** One persisted reasoning round — `n` is kept so a restored block still
 *  reads top to bottom in round order (`readReasoningFrame`'s own reason,
 *  §10.3), `text` is truncated to `MAX_PERSISTED_REASONING_CHARS`. */
type PersistedReasoningRound = { n: number; text: string };

type PersistedFeed = {
  turn: number;
  anchorId: string;
  steps: PersistedStep[];
  summary: ActivitySummary;
  /** Absent, not `[]`, when the turn had no reasoning — "an empty block is
   *  not persisted" (§10.9, R8) applies per surface, not just to the whole
   *  feed. */
  reasoning?: PersistedReasoningRound[];
  reasoningMs?: number | null;
};

function isPersistedStep(v: unknown): v is PersistedStep {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return typeof o.icon === 'string' && typeof o.text === 'string';
}

function isPersistedReasoningRound(v: unknown): v is PersistedReasoningRound {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return typeof o.n === 'number' && typeof o.text === 'string';
}

function isPersistedFeed(v: unknown): v is PersistedFeed {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.turn === 'number' &&
    typeof o.anchorId === 'string' &&
    Array.isArray(o.steps) &&
    o.steps.every(isPersistedStep) &&
    typeof o.summary === 'object' &&
    o.summary !== null &&
    (o.reasoning === undefined || (Array.isArray(o.reasoning) && o.reasoning.every(isPersistedReasoningRound))) &&
    (o.reasoningMs === undefined || o.reasoningMs === null || typeof o.reasoningMs === 'number')
  );
}

/** Best-effort write; every failure means "no feed after reload", never a
 *  thrown error (§2.3, §5.2, risk §8). */
function persist(threadId: string, feeds: Feed[]): void {
  try {
    const finished: PersistedFeed[] = feeds
      .filter((f) => f.finished && f.anchorId && (f.steps.length > 0 || f.reasoning.length > 0))
      .slice(-MAX_PERSISTED_TURNS)
      .map((f) => ({
        turn: f.turn,
        anchorId: f.anchorId!,
        steps: f.steps.map((s) => ({ icon: s.icon, text: s.text })),
        summary: f.summary ?? summaryFromSteps(f),
        // Most recent rounds first — the same "drop the oldest" bias the
        // turn budget below already has.
        ...(f.reasoning.length > 0
          ? {
              reasoning: f.reasoning
                .slice(-MAX_PERSISTED_REASONING_ROUNDS)
                .map((r) => ({ n: r.n, text: r.text.slice(0, MAX_PERSISTED_REASONING_CHARS) })),
              reasoningMs: f.reasoningMs,
            }
          : {}),
      }));
    let body = JSON.stringify(finished);
    // A hard byte ceiling: drop the oldest turns until it fits rather than
    // fail the write outright — a shorter history beats none.
    while (body.length > MAX_PERSISTED_BYTES && finished.length > 0) {
      finished.shift();
      body = JSON.stringify(finished);
    }
    window.localStorage.setItem(PERSIST_PREFIX + threadId, body);
  } catch {
    // localStorage absent, full or blocked — the feed is simply not there
    // after a reload, which is a valid, honestly-degraded state (§2.3).
  }
}

export const useActivity = create<ActivityState>((set, get) => ({
  feeds: [],

  begin: (turn) =>
    set((state) => {
      if (state.feeds.some((f) => f.turn === turn)) return state;
      return { feeds: [...state.feeds, makeFeed(turn)] };
    }),

  apply: (frame, turn) =>
    set((state) => {
      const feeds = state.feeds.slice();
      let index = feeds.findIndex((f) => f.turn === turn);
      if (index === -1) {
        feeds.push(makeFeed(turn));
        index = feeds.length - 1;
      }
      const feed: Feed = { ...feeds[index]!, steps: feeds[index]!.steps.slice(), hasFrames: true };

      if (frame.state === 'running') {
        for (const step of frame.steps) applyStep(feed, step);
      } else if (frame.state === 'finished') {
        feed.finished = true;
        if (frame.summary) feed.summary = frame.summary;
      }
      // `started` needs nothing beyond the feed existing (c) — already done above.

      feeds[index] = feed;
      return { feeds };
    }),

  applyReasoning: (frame, turn) =>
    set((state) => {
      const feeds = state.feeds.slice();
      let index = feeds.findIndex((f) => f.turn === turn);
      if (index === -1) {
        feeds.push(makeFeed(turn));
        index = feeds.length - 1;
      }
      const existing = feeds[index]!;
      // A straggler frame landing on an already-finished turn (§10.3: the
      // summariser call races the turn's end — its 6 s budget outlives a
      // fast, single-round turn routinely, not as an edge case) must not
      // leave the block stuck open: `finish()` only schedules a collapse
      // timer for a feed that ALREADY had `hasReasoningFrames` at that
      // moment, so a turn whose first-ever reasoning frame arrives after
      // `finish()` gets no timer, ever — `reasoningCollapsed` stays the
      // `makeFeed()` default of `false` forever. Caught on review (!265,
      // canvas-activity-design): it was never opened by a live turn, so
      // there is no "let the last line be read" argument for shipping it
      // expanded. Scoped to the FIRST frame only — a later straggler (round
      // 2 arriving after round 1 already forced this) or a visitor who
      // tapped it back open must not be slammed shut under them.
      const isFirstReasoningFrame = !existing.hasReasoningFrames;
      const feed: Feed = { ...existing, reasoning: existing.reasoning.slice(), hasReasoningFrames: true };

      if (frame.state === 'round') {
        const at = feed.reasoning.findIndex((r) => r.n === frame.n);
        if (at === -1) {
          feed.reasoning.push({ n: frame.n, text: frame.text });
          // Frames race (§10.3) — kept sorted so the block reads in round
          // order no matter which one landed last.
          feed.reasoning.sort((a, b) => a.n - b.n);
        } else {
          feed.reasoning[at] = { n: frame.n, text: frame.text }; // idempotent by `n`
        }
      } else {
        feed.reasoningDone = true;
        feed.reasoningMs = frame.ms;
      }

      if (feed.finished && isFirstReasoningFrame) feed.reasoningCollapsed = true;

      feeds[index] = feed;
      return { feeds };
    }),

  finish: (turn, opts = {}) => {
    set((state) => ({
      feeds: state.feeds.map((feed) => {
        if (feed.turn !== turn) return feed;
        const summary = feed.summary ?? (feed.steps.length > 0 ? summaryFromSteps(feed) : null);
        // Same fallback shape as `summary` above: a killed container or a
        // dropped stream must still close the block, not leave its spinner
        // running forever (§2.11's rule, applied to reasoning).
        const reasoningDone = feed.hasReasoningFrames ? true : feed.reasoningDone;
        const reasoningMs =
          feed.hasReasoningFrames && feed.reasoningMs === null
            ? Math.max(0, Date.now() - feed.startedAt)
            : feed.reasoningMs;
        return {
          ...feed,
          finished: true,
          summary,
          reasoningDone,
          reasoningMs,
          anchorId: opts.anchorId !== undefined ? opts.anchorId : feed.anchorId,
        };
      }),
    }));
    clearCollapseTimer(turn);
    collapseTimers.set(
      turn,
      setTimeout(() => {
        collapseTimers.delete(turn);
        set((state) => ({
          feeds: state.feeds.map((f) => (f.turn === turn ? { ...f, collapsed: true } : f)),
        }));
      }, AUTO_COLLAPSE_MS),
    );
    // The reasoning block's own timer, scheduled at the SAME delay (§10.4:
    // "тот же 600-мс приём") but tracked separately so `toggleReasoning`
    // never cancels the activity feed's pending collapse and vice versa.
    // Only a turn that actually had a reasoning frame gets one — nothing to
    // collapse otherwise.
    clearReasoningCollapseTimer(turn);
    if (get().feeds.find((f) => f.turn === turn)?.hasReasoningFrames) {
      reasoningCollapseTimers.set(
        turn,
        setTimeout(() => {
          reasoningCollapseTimers.delete(turn);
          set((state) => ({
            feeds: state.feeds.map((f) => (f.turn === turn ? { ...f, reasoningCollapsed: true } : f)),
          }));
        }, AUTO_COLLAPSE_MS),
      );
    }
    if (opts.threadId) persist(opts.threadId, get().feeds);
  },

  toggle: (turn) => {
    clearCollapseTimer(turn); // a manual tap wins over the pending auto-collapse
    set((state) => ({
      feeds: state.feeds.map((f) => (f.turn === turn ? { ...f, collapsed: !f.collapsed } : f)),
    }));
  },

  toggleReasoning: (turn) => {
    clearReasoningCollapseTimer(turn);
    set((state) => ({
      feeds: state.feeds.map((f) => (f.turn === turn ? { ...f, reasoningCollapsed: !f.reasoningCollapsed } : f)),
    }));
  },

  restore: (threadId) => {
    try {
      const raw = window.localStorage.getItem(PERSIST_PREFIX + threadId);
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      // BLOCKER, found on review: `p.turn` is the PREVIOUS session's turn
      // numbering, and `chatStore.restore()` never touches `state.turn` — it
      // stays at its initial 0 (`chatStore.ts:128`). So the first question
      // asked after an F5 runs `beginTurn()` → `turn: 1`, exactly the number a
      // restored feed already held: `begin(1)` early-returns on it, the live
      // steps get appended onto a feed that is already `finished`/`collapsed`
      // and anchored to a bubble from the OLD session, and `Thread.tsx` then
      // draws that one feed twice — once via `anchoredTurn`, once as the
      // "live" one. Nothing reads a restored feed's `turn` as a turn number
      // (`Thread.tsx` reaches it only through `anchorId`, `toggle`/`ActivityFeed`
      // just need it unique) — so it is stamped NEGATIVE here, a range no real
      // turn (which starts at 1 and only grows) can ever collide with.
      const restored: Feed[] = parsed.filter(isPersistedFeed).map((p, i) => ({
        turn: -(i + 1),
        steps: p.steps.map((step, j) => ({
          id: `restored:${p.anchorId}:${j}`,
          phase: 'done',
          text: step.text,
          icon: step.icon,
          ms: null,
          group: 0,
        })),
        openGroup: 0,
        startedAt: 0,
        finished: true,
        summary: p.summary,
        collapsed: true,
        anchorId: p.anchorId,
        hasFrames: true,
        // §10.9/R8: `n` is kept (not re-minted like step ids) — nothing
        // upserts into a restored feed's rounds again, and the block only
        // ever reads them in order.
        reasoning: (p.reasoning ?? []).map((r) => ({ n: r.n, text: r.text })),
        reasoningDone: true,
        reasoningMs: p.reasoningMs ?? null,
        reasoningCollapsed: true,
        hasReasoningFrames: (p.reasoning ?? []).length > 0,
      }));
      if (restored.length === 0) return;
      // Dedup by ANCHOR, not by the (freshly re-minted, so never stable
      // across calls) synthetic turn number — `restore()` can run twice
      // (React StrictMode's double-invoked effect), and a turn-keyed dedup
      // would just accumulate a second copy of every historical feed.
      set((state) => {
        const anchors = new Set(restored.map((r) => r.anchorId));
        return {
          feeds: [...restored, ...state.feeds.filter((f) => !(f.anchorId && anchors.has(f.anchorId)))],
        };
      });
    } catch {
      // A corrupt or inaccessible entry means no restored feed — never a throw.
    }
  },

  reset: () => {
    clearAllCollapseTimers();
    set({ feeds: [] });
  },
}));
